import React, { useState, useCallback, useRef } from 'react'

import CloseIcon from './CloseIcon'

import * as Styled from './WriteReviewForm.styles'

const WriteReviewForm = ({
  formData,
  setInput,
  setCheckbox,
  validateInput,
  onRatingChange,
  onAddPhoto,
  onRemovePhotoClick,
  onSubmit
}) => {
  const [writingGuidelines, setWritingGuidelines] = useState(false)
  const [photoGuidelines, setPhotoGuidelines] = useState(false)
  const photoUploaderRef = useRef(null)

  const onWritingGuidelinesToggle = useCallback(() => setWritingGuidelines(state => !state))
  const onPhotoGuidelinesToggle = useCallback(() => setPhotoGuidelines(state => !state))

  const getRatingMessage = rating =>
    ({
      1: 'Poor',
      2: 'Fair',
      3: 'Average',
      4: 'Good',
      5: 'Excellent'
    }[rating])

  return (
    <Styled.Form
      onSubmit={event => {
        event.preventDefault()
        onSubmit()
      }}
      method='post'
      data-test='write-a-review-form'
    >
      <Styled.FormGroup70>
        <Styled.LabelSpan id='overall-rating-label'>Overall rating</Styled.LabelSpan>
        <Styled.RatingWrapper>
          <Styled.Rating
            role='slider'
            aria-labelledby='overall-rating-label'
            id='overall-rating'
            rating={0}
            reviewsCount={0}
            large
            readOnly={false}
            message={getRatingMessage(formData.overallRating)}
            error={!!formData.overallRatingError}
            onRatingChange={onRatingChange('overallRating')}
            onBlur={validateInput('overallRating')}
            onMouseLeave={validateInput('overallRating')}
          />
        </Styled.RatingWrapper>
        {formData.overallRatingError && <Styled.ErrorMessage>{formData.overallRatingError}</Styled.ErrorMessage>}
      </Styled.FormGroup70>
      <Styled.FormGroup70>
        <Styled.InputField
          type='text'
          name='title'
          label='Review title'
          hint='Example: Great fit, really comfortable'
          optional='Optional'
          value={formData.reviewTitle}
          onChange={setInput('reviewTitle')}
        />
      </Styled.FormGroup70>
      <Styled.FormGroup70>
        <Styled.Label htmlFor='review'>Review</Styled.Label>
        <Styled.Hint>
          Review tip: Keep focus on the product, what’s great about it, what’s not so great.
          <Styled.GuidelinesToggler onClick={onWritingGuidelinesToggle}>Need more tips?</Styled.GuidelinesToggler>
        </Styled.Hint>
        {writingGuidelines && (
          <Styled.Guidelines>
            <Styled.SubHeading>Writing guidelines</Styled.SubHeading>
            <Styled.GuidelinesParagraph>We’d like to publish your review, so please</Styled.GuidelinesParagraph>
            <ul>
              <li>Keep your review focused on the product</li>
              <li>
                Avoid writing about customer service, contact us instead if you have issues requireing immediate
                attention
              </li>
              <li>Refrain from mentioning competitors or the specific price you paid for the product</li>
              <li>Do not include any personally identifiable information, such as full names</li>
            </ul>
          </Styled.Guidelines>
        )}
        <Styled.ReviewTextarea
          id='review'
          maxLength='10000'
          value={formData.review}
          onChange={setInput('review')}
          isError={!!formData.reviewError}
          onBlur={validateInput('review')}
          aria-invalid={!!formData.reviewError}
          aria-required
        />
        {formData.reviewError && (
          <Styled.ErrorMessage>
            {formData.reviewError}
            <Styled.WordCount>
              {formData.review.length < 20 && `${20 - formData.review.length} characters too short`}
            </Styled.WordCount>
          </Styled.ErrorMessage>
        )}
      </Styled.FormGroup70>
      <Styled.FormGroup70>
        <Styled.LabelSpan id='add-photos-label'>
          Add up to 6 photos <Styled.OptionalLabel>Optional</Styled.OptionalLabel>
        </Styled.LabelSpan>
        <Styled.ImageUploaderInput
          name='add-photos'
          onChange={event => {
            onAddPhoto(event)
            // Reset file input
            photoUploaderRef.current.value = null
          }}
          aria-labelledby='add-photos-label'
          ref={photoUploaderRef}
          aria-hidden
        />
        <Styled.ImageUploaderButton
          type='button'
          kind='secondary'
          onClick={() => {
            photoUploaderRef.current.click()
          }}
        >
          Add photo
        </Styled.ImageUploaderButton>
        <Styled.GuidelinesToggler onClick={onPhotoGuidelinesToggle}>
          Please read photo guidelines
        </Styled.GuidelinesToggler>
        {photoGuidelines && (
          <Styled.Guidelines>
            <Styled.SubHeading>Photo guidelines</Styled.SubHeading>
            <Styled.GuidelinesParagraph>We’d love to see how you style Tu, so please:</Styled.GuidelinesParagraph>
            <ul>
              <li>Do not submit images of children under 18</li>
              <li>Send the original file, straight from your device</li>
              <li>Only submit photos that you created</li>
              <li>Only show images of yourself and no-one else</li>
              <li>Do not submit photos featuring nudity and partial nudity</li>
            </ul>
            <Styled.GuidelinesParagraph>For your photos, please:</Styled.GuidelinesParagraph>
            <ul>
              <li>Make sure these are at least 60 pixels within width and height</li>
              <li>Only send photos in BMP, PNG, GIF or JPEG format</li>
              <li>Only send 6 photos maximum, 5MB or less per photo - we’d hate your shots to go to waste</li>
              <li>Note that uploaded images become the property of Tu Clothing</li>
            </ul>
          </Styled.Guidelines>
        )}
        {formData.photos && (
          <Styled.PhotoContainer>
            {formData.photos.map(photo => (
              <Styled.ThumbnailContainer key={photo.id}>
                <Styled.Thumbnail src={photo.imgSrc} alt={photo.name} />
                <Styled.IconWrapper
                  onClick={onRemovePhotoClick(photo.id)}
                  onKeyPress={event => {
                    event.preventDefault()
                    if (event.key === 'Enter' || event.key === ' ') {
                      onRemovePhotoClick(photo.id)()
                    }
                  }}
                >
                  <CloseIcon />
                </Styled.IconWrapper>
              </Styled.ThumbnailContainer>
            ))}
          </Styled.PhotoContainer>
        )}
      </Styled.FormGroup70>
      <Styled.Divider />
      <Styled.FormGroupFull>
        <Styled.LabelSpan>
          Would you recommend this product to a friend? <Styled.OptionalLabel>Optional</Styled.OptionalLabel>
        </Styled.LabelSpan>
        <Styled.RecommendItem first>
          <Styled.RecommendInput
            name='isrecommended'
            id='recommended-yes'
            value='yes'
            defaultChecked={formData.recommended === 'yes'}
            onClick={setInput('recommended')}
          />
          <Styled.RecommendLabel htmlFor='recommended-yes'>Yes</Styled.RecommendLabel>
        </Styled.RecommendItem>
        <Styled.RecommendItem>
          <Styled.RecommendInput
            name='isrecommended'
            id='recommended-no'
            value='no'
            defaultChecked={formData.recommended === 'no'}
            onClick={setInput('recommended')}
          />
          <Styled.RecommendLabel htmlFor='recommended-no'>No</Styled.RecommendLabel>
        </Styled.RecommendItem>
      </Styled.FormGroupFull>
      <Styled.FormGroup60>
        <Styled.InputRange
          label='Fit'
          optional='Optional'
          options={[
            { value: 0, label: 'Too small' },
            { value: 1, label: '' },
            { value: 2, label: 'True to size' },
            { value: 3, label: '' },
            { value: 4, label: 'Too big' }
          ]}
          value={formData.fit}
          onChange={setInput('fit')}
        />
      </Styled.FormGroup60>
      <Styled.FormGroup60>
        <Styled.LabelSpan id='quality-label'>
          Quality <Styled.OptionalLabel>Optional</Styled.OptionalLabel>
        </Styled.LabelSpan>
        <Styled.RatingWrapper>
          <Styled.Rating
            role='slider'
            aria-labelledby='quality-label'
            id='quality'
            rating={0}
            reviewsCount={0}
            large
            readOnly={false}
            message={getRatingMessage(formData.quality)}
            onRatingChange={onRatingChange('quality')}
          />
        </Styled.RatingWrapper>
      </Styled.FormGroup60>
      <Styled.FormGroup60>
        <Styled.LabelSpan id='comfort-label'>
          Comfort <Styled.OptionalLabel>Optional</Styled.OptionalLabel>
        </Styled.LabelSpan>
        <Styled.RatingWrapper>
          <Styled.Rating
            role='slider'
            aria-labelledby='comfort-label'
            id='comfort'
            rating={0}
            reviewsCount={0}
            large
            readOnly={false}
            message={getRatingMessage(formData.comfort)}
            onRatingChange={onRatingChange('comfort')}
          />
        </Styled.RatingWrapper>
      </Styled.FormGroup60>
      <Styled.FormGroup60>
        <Styled.LabelSpan id='value-label'>
          Value <Styled.OptionalLabel>Optional</Styled.OptionalLabel>
        </Styled.LabelSpan>
        <Styled.RatingWrapper>
          <Styled.Rating
            role='slider'
            aria-labelledby='value-label'
            id='value'
            rating={0}
            reviewsCount={0}
            large
            readOnly={false}
            message={getRatingMessage(formData.value)}
            onRatingChange={onRatingChange('value')}
          />
        </Styled.RatingWrapper>
      </Styled.FormGroup60>
      <Styled.Divider />
      <Styled.FormGroup60>
        <Styled.SelectInput
          name='age'
          label='Age'
          optional='Optional'
          placeholder='Select'
          value={formData.age}
          onChange={setInput('age')}
          options={[
            { title: '16 - 24', value: '16 to 24' },
            { title: '25 - 34', value: '25 to 34' },
            { title: '35 - 55', value: '35 to 54' },
            { title: '55 - 74', value: '55 to 74' },
            { title: '75 and over', value: '75 and over' }
          ]}
        />
      </Styled.FormGroup60>
      <Styled.FormGroup60>
        <Styled.InputField
          type='text'
          name='nickname'
          label='Nickname'
          value={formData.nickname}
          isError={!!formData.nicknameError}
          onChange={setInput('nickname')}
          onBlur={validateInput('nickname')}
          aria-required
        />
        {formData.nicknameError && <Styled.ErrorMessage>{formData.nicknameError}</Styled.ErrorMessage>}
      </Styled.FormGroup60>
      <Styled.FormGroup60>
        <Styled.InputField
          type='email'
          name='email'
          label='Email'
          hint='We will ONLY use your email to notify you in regards to your review'
          value={formData.email}
          isError={!!formData.emailError}
          onChange={setInput('email')}
          onBlur={validateInput('email')}
          aria-required
        />
        {formData.emailError && <Styled.ErrorMessage>{formData.emailError}</Styled.ErrorMessage>}
      </Styled.FormGroup60>
      <Styled.FormGroup60>
        <Styled.TacCheckbox
          id='tac'
          name='terms-and-conditions'
          label={
            <>
              I agree to the terms & conditions
              <br />
              (you may receive emails regarding this review, any email will include the ability to opt out of future
              communication)
            </>
          }
          onChange={setCheckbox('termsAndConditions')}
          checked={formData.termsAndConditions}
          isError={!!formData.termsAndConditionsError}
        />
        {formData.termsAndConditionsError && (
          <Styled.ErrorMessage>{formData.termsAndConditionsError}</Styled.ErrorMessage>
        )}
      </Styled.FormGroup60>
      <Styled.FormGroup60>
        <Styled.Button kind='primary' type='submit' full>
          Post review
        </Styled.Button>
      </Styled.FormGroup60>
    </Styled.Form>
  )
}

export default WriteReviewForm
